import { Component, OnInit, effect, inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CodeEditorModule } from '@ngstack/code-editor';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { ConfirmationDialogComponent, ModalState } from '@bn/web-ui/dialog';
import { CustomPageType, ShieldApiStore, ShieldCustomPagesApiStore } from '../../../../stores';

export type WhiteLabelPageType = CustomPageType;

const PAGE_CONFIG: Record<WhiteLabelPageType, { title: string; description: string }> = {
  challenge: {
    title: 'Edit challenge page',
    description: 'Served with JS challenges (bot detection & managed challenges).',
  },
  block: {
    title: 'Edit block page',
    description: 'Served when a request is blocked by WAF, access list or bot detection.',
  },
  ratelimit: {
    title: 'Edit rate limiting page',
    description: 'Served when a visitor breaches a rate-limit rule.',
  },
};

@Component({
  templateUrl: './app-shield-white-label-page-modal.component.html',
  styleUrls: ['./app-shield-white-label-page-modal.component.scss'],
  imports: [CodeEditorModule, ButtonComponent, FaIconComponent],
  standalone: true,
})
export class AppShieldWhiteLabelPageModalComponent implements OnInit {
  readonly #customPagesStore = inject(ShieldCustomPagesApiStore);
  readonly #dialog = inject(BsModalRef<AppShieldWhiteLabelPageModalComponent>);
  readonly #fb = inject(NonNullableFormBuilder);
  readonly #modalService = inject(BsModalService);
  readonly #shieldStore = inject(ShieldApiStore);

  public shieldZoneId = this.#shieldStore.selectedEntityId();

  public data?: ModalState;
  public pageType?: WhiteLabelPageType;
  public config = PAGE_CONFIG.challenge;
  public isLoading = false;

  public codeModel = {
    language: 'text/html',
    uri: 'custom-page.html',
    value: '',
  };

  public readonly codeEditorOptions = {
    contextmenu: false,
    minimap: { enabled: false },
    scrollbar: { vertical: 'auto' },
    overviewRulerBorder: false,
    renderLineHighlight: 'none',
    automaticLayout: true,
    scrollBeyondLastLine: false,
  };

  public form = this.#fb.group({
    htmlContent: this.#fb.control(''),
  });

  constructor() {
    effect(() => {
      if (!this.pageType) return;
      const htmlContent = this.#customPagesStore.customPages()[this.pageType];
      if (htmlContent !== null) {
        this.codeModel = { ...this.codeModel, value: htmlContent };
        this.form.controls.htmlContent.setValue(htmlContent);
      }
    });
  }

  public ngOnInit(): void {
    this.pageType = this.data?.extra?.['pageType'] as WhiteLabelPageType;
    this.config = PAGE_CONFIG[this.pageType] ?? PAGE_CONFIG.challenge;

    const shieldZoneId = Number(this.shieldZoneId());
    if (shieldZoneId && this.pageType) {
      this.#customPagesStore.loadCustomPage({ shieldZoneId, pageType: this.pageType });
    }
  }

  public onSave(): void {
    const shieldZoneId = Number(this.shieldZoneId());
    if (!shieldZoneId || !this.pageType) return;

    this.#customPagesStore.saveCustomPage({
      shieldZoneId,
      pageType: this.pageType,
      htmlContent: this.form.controls.htmlContent.value,
    });
    this.#dialog.hide();
  }

  public get hasCustomPage(): boolean {
    return this.pageType ? this.#customPagesStore.customPages()[this.pageType] !== null : false;
  }

  public onResetToDefault(): void {
    const shieldZoneId = Number(this.shieldZoneId());
    if (!shieldZoneId || !this.pageType) return;

    const confirmRef = this.#modalService.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          title: 'Reset to default?',
          description: 'This will replace your custom page with the default template. This action cannot be undone.',
          confirmButtonName: 'Reset to default',
          contextType: 'danger',
          event: { action: 'confirm' },
          textCenter: true,
        },
      },
    });

    confirmRef.content?.getOnAction$().subscribe(({ action }) => {
      if (action === 'confirm') {
        this.#customPagesStore.resetToDefault({ shieldZoneId, pageType: this.pageType! });
        this.#dialog.hide();
      }

      confirmRef.hide();
    });
  }

  public onClose(): void {
    this.#dialog.hide();
  }
}
